import { useState, useContext, useEffect } from "react";
import { TransferContext } from "../../context/TransferContext";
import connectOldAccountValidations from "../../utils/connectOldAccountValidation";
import SourceAccount from "../../class/sourceAccount";
import { AppContext } from "../../context/AppContext";
import TransferDetails, {
  STEP_OLD_ACCOUNT_COMPLETED_HOME,
  STEP_OLD_ACCOUNT_EMPLOYMENT_DETAILS,
  STEP_OLD_ACCOUNT_HAS_ACCOUNT_STATEMENT,
  STEP_OLD_ACCOUNT_HAS_ANOTHER_ACCOUNT,
  STEP_OLD_ACCOUNT_PROVIDER_SEARCH,
  STEP_OLD_ACCOUNT_PROVIDER_KNOWN_SCREEN,
  STEP_OLD_ACCOUNT_SPONSOR_NAME,
  STEP_OLD_ACCOUNT_UPLOAD_STATEMENT,
  STEP_OLD_ACCOUNT_UPLOAD_STATEMENT_SUCCEED,
  STEP_OLD_ACCOUNT_CONFIRM,
  STEP_OLD_ACCOUNT_FLASH_LOADING,
  STEP_DESTINATION_IRA_FOUND,
  STEP_DESTINATION_OPTIONS,
  STEP_DESTINATION_FLASH_LOADING_TRANFER_IN,
  STEP_DESTINATION_ACCOUNT,
} from "../../class/transferDetails";

import {
  BRIDGE_DEMO,
  BRIDGE_DEVELOPMENT,
  TRANSFER_FLOW_IN,
  TRANSFER_FLOW_OUT,
  UNAUTHORIZED_ERROR_MESSAGE,
  UNEXPECTED_ERROR_MESSAGE,
} from "../../typings";
import getAPI from "../../utils/getAPI";
import { sendSentryError } from "../../lib/sentry";
import Analytics from "../../utils/productAnalytics";
import manualWait from "../../utils/manualWait";
import axiosWithRetry from "../../utils/axiosRetry";
import { type UploadedFile } from "@manifesthhqinc/ui-library";
import { FileUploadStatus, SCAN_FAILED, UPLOAD_FAILED, UPLOAD_SUCCESS, FileScanResponse } from "../../class/file";

const generateRandomSourceAccountId = () => Math.floor(Math.random() * 9999);

export type OldAccountScreensProps = ReturnType<typeof useOldAccountDetails>;

export type Validation = {
  [key in keyof SourceAccount]?: string | undefined | boolean;
};

const useOldAccountDetails = () => {
  const { transferIntentId, clientId, transferFlow } = useContext(AppContext);
  const {
    updateTransferDetails,
    transferDetails,
    currentOldAccount,
    refreshTransferDetails,
    goToScreen,
    currentScreen,
    userDetails,
    setScreenRef,
    isLoading,
    setIsLoading,
    employerAPI,
    userId,
    setUserId,
    setShowError,
    providerList,
    transferIntentDetails,
    setHasMoreThanOneSourceAccount,
    uploadSourceAccountFile,
    removeSourceAccountStatement,
    isDestinationAnimationLoaded,
    isOldAccountAnimationLoaded,
    setIsOldAccountAnimationLoaded,
  } = useContext(TransferContext);

  const { partnerConfig, appEnv } = useContext(AppContext);

  const [currentOldAccountDetails, setCurrentOldAccountDetails] = useState<SourceAccount>(currentOldAccount);
  const [shouldScreenContinue, setShouldScreenContinue] = useState<boolean>(false);
  const [statementFile, setStatementFile] = useState<File>();
  const [uploadErrorMessage, setUploadErrorMessage] = useState<string | undefined>();
  const [planSponsorId, setPlanSponsorId] = useState<string>(""); // we will need to store the plan sponsor id here, since it is not stored in backend, and potentially get overrided by refreshing details
  const [validation, setValidation] = useState<Validation>();
  const [isUploading, setIsUploading] = useState<boolean>(false);
  const [uploadedFiles, setUploadedFiles] = useState<UploadedFile[]>([]);
  const [isSaving, setIsSaving] = useState<boolean>(false);

  const isValidYear = (yearString?: string): boolean => {
    if (!yearString) return false;
    const yearNum = parseInt(yearString);
    if (yearNum < 1925) return false;
    return true;
  };

  const validateEmploymentDetails = (
    formDetails: Partial<SourceAccount>,
  ): {
    isValidated: boolean;
    formValidation: Record<string, string>;
  } => {
    const formValidation: Record<string, string> = {
      startYear: !isValidYear(formDetails.startYear)
        ? "Please enter a valid year"
        : connectOldAccountValidations.validateField("startYear", formDetails),
      terminatedYear: !isValidYear(formDetails.terminatedYear)
        ? "Please enter a valid year"
        : connectOldAccountValidations.validateField("terminatedYear", formDetails),
    };
    return { isValidated: Object.values(formValidation).filter((e) => e).length === 0, formValidation };
  };

  const validateForm = (formDetails: Partial<SourceAccount>): boolean => {
    switch (currentScreen) {
      case STEP_OLD_ACCOUNT_SPONSOR_NAME:
        return connectOldAccountValidations.sponsorName(new SourceAccount(formDetails)) === "";
      case STEP_OLD_ACCOUNT_PROVIDER_SEARCH:
        return (
          connectOldAccountValidations.endYear(new SourceAccount(formDetails)) === "" &&
          connectOldAccountValidations.startYear(new SourceAccount(formDetails)) === ""
        );
      case STEP_OLD_ACCOUNT_UPLOAD_STATEMENT:
      case STEP_OLD_ACCOUNT_UPLOAD_STATEMENT_SUCCEED:
        return formDetails.sourceStatementFileNames?.length !== 0;
      default:
        return true;
    }
  };

  useEffect(() => {
    const isValidated = validateForm(currentOldAccountDetails);
    setShouldScreenContinue(isValidated);
  }, [currentScreen, currentOldAccountDetails]);

  const showFinishScreen = () => {
    goToScreen(STEP_OLD_ACCOUNT_COMPLETED_HOME);
  };

  const createOrFindTransferByIntent = async (): Promise<{
    transferDetailsRes: TransferDetails;
    userIdRes: string;
  }> => {
    let catchReason = "";
    // CHECK IF TRANSFER EXIST
    try {
      const queryTransferDetailsRes = await getAPI(appEnv || BRIDGE_DEVELOPMENT)({
        method: "GET",
        url: `/api/userIdentity/transferIntent/${transferIntentId}/transfer`,
      });
      return {
        transferDetailsRes: TransferDetails.fromAPI(queryTransferDetailsRes.data.data),
        userIdRes: queryTransferDetailsRes.data.data.userId,
      };
    } catch (e: any) {
      // do nothing
      if (e.response.status === 404) {
        catchReason = "NOT_FOUND";
      }
      if (e.response.status === 401) {
        setShowError(UNAUTHORIZED_ERROR_MESSAGE);
        return {
          transferDetailsRes: new TransferDetails(),
          userIdRes: "",
        };
      }
    }
    if (catchReason === "NOT_FOUND") {
      // create a new transfer
      try {
        const newTransferDetailsRes = await getAPI(appEnv || BRIDGE_DEVELOPMENT)({
          method: "POST",
          url: `/api/userIdentity/transfer?transferIntentId=${transferIntentId}&clientId=${clientId}`,
        });
        return {
          transferDetailsRes: TransferDetails.fromAPI(newTransferDetailsRes.data.data),
          userIdRes: newTransferDetailsRes.data.data.userId,
        };
      } catch (e: any) {
        if (e.response.status === 400 && e.response.data.responseCode === "E1017") {
          catchReason = "TRANSFER_EXIST";
        }
      }
    }
    if (catchReason === "TRANSFER_EXIST") {
      try {
        const queryTransferDetailsRes = await getAPI(appEnv || BRIDGE_DEVELOPMENT)({
          method: "GET",
          url: `/api/userIdentity/transferIntent/${transferIntentId}/transfer`,
        });
        return {
          transferDetailsRes: TransferDetails.fromAPI(queryTransferDetailsRes.data.data),
          userIdRes: queryTransferDetailsRes.data.data.userId,
        };
      } catch (e: any) {
        setShowError(UNEXPECTED_ERROR_MESSAGE);
        sendSentryError(`Unable to create a transfer with transferIntentId, ${transferIntentId}. ${e.message}`);
      }
    }

    return {
      transferDetailsRes: new TransferDetails(),
      userIdRes: "",
    };
  };

  const updateSourceAccountsAndRefreshDetails = async (
    updatedOldAccountDetails: SourceAccount,
  ): Promise<{
    sourceAccount: SourceAccount;
    transferDetails: TransferDetails;
  }> => {
    if (appEnv === BRIDGE_DEMO) {
      await manualWait(100);
      const transferDetailsUpdated = transferDetails;

      const updatedSourceAccountDetails = updatedOldAccountDetails;
      if (transferDetailsUpdated.transferId === -1) {
        transferDetailsUpdated.transferId = generateRandomSourceAccountId();
      }
      if (updatedSourceAccountDetails.sourceAccountId === -1) {
        updatedSourceAccountDetails.sourceAccountId = generateRandomSourceAccountId();
      }
      // If more than one source account
      if (transferDetailsUpdated.sourceAccounts.length >= 1) {
        const currentSourceAccountIndex = transferDetails.sourceAccounts.findIndex(
          (s) => s.sourceAccountId === updatedSourceAccountDetails.sourceAccountId,
        );
        if (currentSourceAccountIndex > -1) {
          transferDetailsUpdated.sourceAccounts[currentSourceAccountIndex] = new SourceAccount({
            ...updatedSourceAccountDetails,
          });
        } else {
          transferDetailsUpdated.sourceAccounts = [
            ...transferDetailsUpdated.sourceAccounts,
            new SourceAccount({
              ...updatedSourceAccountDetails,
            }),
          ];
        }
      } else {
        transferDetailsUpdated.sourceAccounts = [new SourceAccount({ ...updatedSourceAccountDetails })];
      }
      updateTransferDetails(transferDetailsUpdated);
      setIsSaving(false);
      return {
        sourceAccount: updatedSourceAccountDetails,
        transferDetails: transferDetailsUpdated,
      };
    } else {
      /* STEP ONE: get current transfer id, source account id, user id
         STEP TWO:
            - if no current transfer id or no user id
                check if there is existing transfer by transfer intent > transferId & userId
                if there is no existing transfer > create a new transfer > transferId & userId
            - if no source account id >
                create new source account
            - if source account id 
                update transfer details */

      // STEP ONE: get transferId, sourceAccountId and userId
      let updatedSourceAccountDetails = updatedOldAccountDetails;
      let currentTransfer: TransferDetails = transferDetails;
      let currentUserId = userId;
      let currentTransferId = currentTransfer.transferId;
      const sourceAccountId = updatedSourceAccountDetails.sourceAccountId;
      // STEP TWO: check and validate transfer id, get transferDetails and userId
      if (currentTransferId === -1 || !currentUserId) {
        // If transfer is not found in the context. Call get transfer first and see if there is an existing transfer
        try {
          const { transferDetailsRes, userIdRes } = await createOrFindTransferByIntent();
          currentUserId = userIdRes;
          currentTransfer = transferDetailsRes;
          currentTransferId = currentTransfer.transferId;
        } catch (e: any) {
          sendSentryError(`Unable to create transfer by transfer intent - ${transferIntentId}. ${e?.message || ""}`);
        }
      }

      if (currentTransfer.transferId === -1 || !currentUserId) {
        // if there is an error in fetching or creating transfer
        setIsSaving(false);
        setShowError("Unable to fetch transfer id and user id");
        return {
          sourceAccount: updatedOldAccountDetails,
          transferDetails: currentTransfer,
        };
      }
      // STEP THREE: check source account id
      try {
        if (sourceAccountId === -1 && currentTransferId !== -1 && currentUserId) {
          // Create source account with transfer id and user id
          const sourceAccountRes = await getAPI(appEnv || BRIDGE_DEVELOPMENT)({
            method: "POST",
            url: `/api/userIdentity/${currentUserId}/transfer/${currentTransferId}/sourceAccount`,
            data: {
              ...SourceAccount.toJSON(updatedSourceAccountDetails),
            },
          });
          updatedSourceAccountDetails = SourceAccount.fromApi(sourceAccountRes.data.data);
          setCurrentOldAccountDetails(updatedSourceAccountDetails);
        } else if (currentTransferId !== -1 && currentUserId && updatedOldAccountDetails.sourceAccountId !== -1) {
          const sourceAccountRes = await getAPI(appEnv || BRIDGE_DEVELOPMENT)({
            method: "POST",
            url: `/api/userIdentity/${currentUserId}/transfer/${currentTransferId}/sourceAccount/${updatedOldAccountDetails.sourceAccountId}/update`,
            data: {
              ...SourceAccount.toJSON(updatedSourceAccountDetails),
            },
          });
          updatedSourceAccountDetails = SourceAccount.fromApi(sourceAccountRes.data.data);
          setCurrentOldAccountDetails(updatedSourceAccountDetails);
        }
      } catch (e: any) {
        if (e.response.status === 401) {
          setShowError(UNAUTHORIZED_ERROR_MESSAGE);
        }
        sendSentryError(`Unable to udpate source account: ${e?.message || ""}`);
      }

      // STEP FOUR: get updated transfer details
      // get transfer details by transferIntentId
      let transferDetailsUpdated = currentTransfer;
      try {
        const transferDetailsRes = await getAPI(appEnv || BRIDGE_DEVELOPMENT)({
          method: "GET",
          url: `/api/userIdentity/transferIntent/${transferIntentId}/transfer`,
        });
        transferDetailsUpdated = TransferDetails.fromAPI(transferDetailsRes.data.data);
        updateTransferDetails(transferDetailsUpdated);
        if (!userId) {
          setUserId(transferDetailsRes.data.data.userId);
        }
      } catch (e) {
        // do nothing
      }

      setIsSaving(false);
      return {
        sourceAccount: updatedOldAccountDetails,
        transferDetails: transferDetailsUpdated,
      };
    }
  };

  const onClickContinue = async () => {
    if (!currentOldAccountDetails) return;
    if (isSaving) return;
    const isValidated = validateForm(currentOldAccountDetails);
    if (!isValidated) {
      // do something
    } else {
      switch (currentScreen) {
        case STEP_OLD_ACCOUNT_SPONSOR_NAME:
          Analytics.track({
            name: "Confirmed old employer name",
          });
          const updatedOldAccountDetails = new SourceAccount({
            sourceAccountId: currentOldAccountDetails.sourceAccountId,
            planSponsor: currentOldAccountDetails.planSponsor,
            planSponsorId: currentOldAccountDetails.planSponsorId,
            transferId: currentOldAccountDetails.transferId,
          });
          setCurrentOldAccountDetails(updatedOldAccountDetails);
          await updateSourceAccountsAndRefreshDetails(updatedOldAccountDetails);
          goToScreen(STEP_OLD_ACCOUNT_HAS_ACCOUNT_STATEMENT);
          break;
        case STEP_OLD_ACCOUNT_PROVIDER_SEARCH:
        case STEP_OLD_ACCOUNT_PROVIDER_KNOWN_SCREEN:
          Analytics.track({
            name: "Continued without old account statement",
          });
          await updateSourceAccountsAndRefreshDetails(currentOldAccountDetails);
          goToScreen(STEP_OLD_ACCOUNT_HAS_ANOTHER_ACCOUNT);
          break;
        case STEP_OLD_ACCOUNT_UPLOAD_STATEMENT:
          Analytics.track({
            name: "Uploaded old account statement",
          });
          goToScreen(STEP_OLD_ACCOUNT_UPLOAD_STATEMENT_SUCCEED);
          break;
        case STEP_OLD_ACCOUNT_UPLOAD_STATEMENT_SUCCEED:
          await updateSourceAccountsAndRefreshDetails(currentOldAccountDetails);
          goToScreen(STEP_OLD_ACCOUNT_HAS_ANOTHER_ACCOUNT);

          break;
        case STEP_OLD_ACCOUNT_COMPLETED_HOME:
          Analytics.track({
            name: "Finished old account section",
          });
          if (transferFlow === TRANSFER_FLOW_IN) {
            goToScreen(
              !isDestinationAnimationLoaded ? STEP_DESTINATION_FLASH_LOADING_TRANFER_IN : STEP_DESTINATION_ACCOUNT,
            );
          } else if (
            transferFlow === TRANSFER_FLOW_OUT &&
            transferIntentDetails?.accounts &&
            transferIntentDetails.accounts[0]
          ) {
            await updateSourceAccountsAndRefreshDetails(currentOldAccountDetails);
            goToScreen(STEP_DESTINATION_IRA_FOUND);
          } else {
            await updateSourceAccountsAndRefreshDetails(currentOldAccountDetails);
            goToScreen(STEP_DESTINATION_OPTIONS);
          }

          break;
        case STEP_OLD_ACCOUNT_HAS_ANOTHER_ACCOUNT:
          showFinishScreen();
          refreshTransferDetails();
          break;
        case STEP_OLD_ACCOUNT_CONFIRM:
          goToScreen(isOldAccountAnimationLoaded ? STEP_OLD_ACCOUNT_COMPLETED_HOME : STEP_OLD_ACCOUNT_FLASH_LOADING);
          break;
        case STEP_OLD_ACCOUNT_FLASH_LOADING:
          goToScreen(STEP_OLD_ACCOUNT_COMPLETED_HOME);
          break;
        default:
          break;
      }
    }
  };

  useEffect(() => {
    if (transferDetails.sourceAccounts.length > 0) {
      const hasIncompleteSourceAccount = transferDetails.sourceAccounts.filter((s) => !s.isCompleted)[0];
      if (hasIncompleteSourceAccount) {
        setCurrentOldAccountDetails(hasIncompleteSourceAccount);
      }
    }
  }, [transferDetails.sourceAccounts]);

  const onClickPrevButton = () => {
    // As there are two different flows for the old account we want to ensure we are using the latest flow to navigate back to the correct previous screen
    const latestSourceAccount = transferDetails.sourceAccounts[transferDetails.sourceAccounts.length - 1];

    switch (currentScreen) {
      case STEP_OLD_ACCOUNT_SPONSOR_NAME:
        goToScreen(STEP_OLD_ACCOUNT_HAS_ANOTHER_ACCOUNT);
        break;
      case STEP_OLD_ACCOUNT_HAS_ACCOUNT_STATEMENT:
        goToScreen(STEP_OLD_ACCOUNT_SPONSOR_NAME);
        break;
      case STEP_OLD_ACCOUNT_EMPLOYMENT_DETAILS:
        goToScreen(STEP_OLD_ACCOUNT_HAS_ACCOUNT_STATEMENT);
        break;
      case STEP_OLD_ACCOUNT_PROVIDER_SEARCH:
      case STEP_OLD_ACCOUNT_PROVIDER_KNOWN_SCREEN:
        goToScreen(STEP_OLD_ACCOUNT_EMPLOYMENT_DETAILS);
        break;
      case STEP_OLD_ACCOUNT_UPLOAD_STATEMENT:
        goToScreen(STEP_OLD_ACCOUNT_HAS_ACCOUNT_STATEMENT);
        break;
      case STEP_OLD_ACCOUNT_UPLOAD_STATEMENT_SUCCEED:
        goToScreen(STEP_OLD_ACCOUNT_UPLOAD_STATEMENT);
        break;
      case STEP_OLD_ACCOUNT_HAS_ANOTHER_ACCOUNT:
        if (latestSourceAccount.providerSkipped === "FALSE") {
          goToScreen(STEP_OLD_ACCOUNT_PROVIDER_KNOWN_SCREEN);
        } else if (latestSourceAccount.statementSkipped === "TRUE") {
          goToScreen(STEP_OLD_ACCOUNT_PROVIDER_SEARCH);
        } else {
          goToScreen(STEP_OLD_ACCOUNT_UPLOAD_STATEMENT_SUCCEED);
        }
        break;
      case STEP_OLD_ACCOUNT_COMPLETED_HOME:
        if (transferFlow === TRANSFER_FLOW_OUT) {
          goToScreen(STEP_OLD_ACCOUNT_CONFIRM);
        } else {
          goToScreen(STEP_OLD_ACCOUNT_HAS_ANOTHER_ACCOUNT);
        }

        break;
      default:
        break;
    }
  };

  const updateOldAccountDetails = (params: Partial<SourceAccount>) => {
    if (params.statementSkipped !== undefined && currentScreen === STEP_OLD_ACCOUNT_HAS_ACCOUNT_STATEMENT) {
      // go to next step
      goToScreen(
        params.statementSkipped === "TRUE" ? STEP_OLD_ACCOUNT_EMPLOYMENT_DETAILS : STEP_OLD_ACCOUNT_UPLOAD_STATEMENT,
      );
    }
    const newOldAccountDetails = new SourceAccount({
      ...currentOldAccountDetails,
      ...params,
    });
    setCurrentOldAccountDetails(newOldAccountDetails);

    if (currentScreen === STEP_OLD_ACCOUNT_SPONSOR_NAME) {
      setPlanSponsorId(params.planSponsorId || "");
    }
  };

  const addAnotherOldAccount = () => {
    setCurrentOldAccountDetails(new SourceAccount());
    setUploadedFiles([]);
    setPlanSponsorId("");
    goToScreen(STEP_OLD_ACCOUNT_SPONSOR_NAME);
    setHasMoreThanOneSourceAccount(true);
  };

  const uploadStatementToAPI = async (file: File): Promise<FileUploadStatus> => {
    if (appEnv === BRIDGE_DEMO) {
      const updatedOldAccountDetails = new SourceAccount({
        ...currentOldAccountDetails,
        statementSkipped: "FALSE",
        sourceStatementFileNames: [file.name],
      });
      updateOldAccountDetails(updatedOldAccountDetails);
      return UPLOAD_SUCCESS;
    } else {
      if (!userId || transferDetails.transferId === -1) return UPLOAD_FAILED;
      const formData = new FormData();
      formData.append("file", file);
      try {
        await getAPI(appEnv)({
          method: "POST",
          url: `/api/userIdentity/${userId}/transfer/${transferDetails.transferId}/sourceAccount/uploadStatementFile/${file.name}`,
          data: formData,
        });
      } catch (e: any) {
        if (e.response.status === 401) {
          setShowError(UNAUTHORIZED_ERROR_MESSAGE);
        }

        sendSentryError(`Unable to upload source account statement. ${e?.message || ""}`);
        return UPLOAD_FAILED;
      }
      try {
        await manualWait(2500);
        const scanDoc = async (): Promise<FileScanResponse> =>
          await getAPI(appEnv)({
            method: "GET",
            url: `/api/userIdentity/${userId}/transfer/${transferDetails.transferId}/sourceAccount/uploadStatementFile/${file.name}/scanStatus`,
          });
        const res = await axiosWithRetry(scanDoc);
        return (res as FileScanResponse).fileSafe ? UPLOAD_SUCCESS : SCAN_FAILED;
      } catch (e) {
        console.error(e);
        return SCAN_FAILED;
      } finally {
        const updatedOldAccountDetails = new SourceAccount({
          ...currentOldAccountDetails,
          statementSkipped: "FALSE",
          sourceStatementFileNames: [file.name],
        });
        updateOldAccountDetails(updatedOldAccountDetails);
      }
    }
  };

  const fetchProvider = async (updatedOldAccountDetails: Partial<SourceAccount>) => {
    // check all fields
    const { isValidated, formValidation } = validateEmploymentDetails(updatedOldAccountDetails);

    if (!isValidated) {
      setValidation(formValidation);
      return;
    }
    setIsLoading(true);
    let updatedAccount = new SourceAccount({ ...updatedOldAccountDetails });
    if (updatedOldAccountDetails.providerName) {
      goToScreen(STEP_OLD_ACCOUNT_PROVIDER_KNOWN_SCREEN);
      setCurrentOldAccountDetails(updatedAccount);
      setIsLoading(false);
      return;
    } else if (planSponsorId && !updatedOldAccountDetails.providerName) {
      const res = await employerAPI.employer.findProviderByEmployerId(planSponsorId);
      if (res.length > 0) {
        updatedAccount = new SourceAccount({
          ...updatedOldAccountDetails,
          providerName: res[0],
        });
      }
      setCurrentOldAccountDetails(updatedAccount);
    }
    goToScreen(STEP_OLD_ACCOUNT_PROVIDER_SEARCH);
    setIsLoading(false);
  };

  const validateField = (key: keyof SourceAccount) => {
    const error = connectOldAccountValidations.validateField(key, currentOldAccountDetails);

    setValidation({
      ...validation,
      [key]: error,
    });
  };

  const handleUploadFile = async (files: UploadedFile[]): Promise<FileUploadStatus> => {
    const updatedSourceAccount = new SourceAccount({
      ...currentOldAccountDetails,
      statementSkipped: "FALSE",
      sourceStatementFileNames: [...currentOldAccountDetails.sourceStatementFileNames, files[0].fileName],
    });
    setCurrentOldAccountDetails(updatedSourceAccount);
    const uploadingFile = {
      file: files[0].file,
      fileName: files[0].fileName,
      id: `${files[0].fileName}`,
      status: "uploading" as const,
      progress: 0,
      fileSize: files[0].fileSize,
    };

    setUploadedFiles([...uploadedFiles, uploadingFile]);
    setIsUploading(true);

    const progressInterval = setInterval(() => {
      setUploadedFiles((prev) =>
        prev.map((file) => {
          if (uploadingFile && file.status === "uploading" && file.progress < 80) {
            return { ...file, progress: Math.min(file.progress + 10, 80) };
          }
          return file;
        }),
      );
    }, 200);
    if (appEnv === BRIDGE_DEMO) {
      await manualWait(3000);

      setUploadedFiles([
        ...uploadedFiles,
        {
          ...uploadingFile,
          progress: 100,
          status: "completed",
        },
      ]);
      clearInterval(progressInterval);
      setIsUploading(false);
      return UPLOAD_SUCCESS;
    } else {
      const status = await uploadSourceAccountFile(uploadingFile);

      if (status === UPLOAD_SUCCESS) {
        setUploadedFiles((prev) =>
          prev.map((uploadedFie) =>
            uploadedFie.id === uploadingFile.id
              ? { ...uploadingFile, status: "completed" as const, progress: 100 }
              : uploadingFile,
          ),
        );
        await updateSourceAccountsAndRefreshDetails(updatedSourceAccount);
      } else {
        setUploadedFiles((prev) => prev.filter((uploadedFie) => uploadedFie.id === uploadingFile.id));
      }
      setIsUploading(false);
      clearInterval(progressInterval);
      return status;
    }
  };

  const handleRemoveFile = async (fileName: string): Promise<void> => {
    const updatedSourceAccountDetails = new SourceAccount({
      ...currentOldAccountDetails,
      sourceStatementFileNames: currentOldAccountDetails.sourceStatementFileNames.filter((e) => e !== fileName),
    });
    setIsUploading(true);
    if (appEnv !== BRIDGE_DEMO) {
      await Promise.all([
        removeSourceAccountStatement(fileName),
        updateSourceAccountsAndRefreshDetails(updatedSourceAccountDetails),
      ]);
    }
    setCurrentOldAccountDetails(updatedSourceAccountDetails);
    setUploadedFiles(uploadedFiles.filter((e) => e.fileName !== fileName));
    setIsUploading(false);
  };

  useEffect(() => {
    if (currentScreen === STEP_OLD_ACCOUNT_CONFIRM) {
      setCurrentOldAccountDetails(
        new SourceAccount({
          ...transferIntentDetails?.transferAccount,
        }),
      );
    }
  }, [currentScreen]);

  return {
    setScreenRef,
    currentSourceAccountStep: currentScreen,
    currentOldAccountDetails,
    onClickContinue,
    onClickPrevButton,
    updateOldAccountDetails,
    addAnotherOldAccount,
    goToScreen,
    shouldScreenContinue,
    validateForm,
    uploadStatementToAPI,
    statementFile,
    setStatementFile,
    handleUploadFile,
    uploadErrorMessage,
    setUploadErrorMessage,
    userDetails,
    fetchProvider,
    partnerConfig,
    isLoading,
    employerAPI,
    validateField,
    validation,
    isUploading,
    isSaving,
    setIsSaving,
    providerList,
    transferFlow,
    uploadedFiles,
    handleRemoveFile,
    isOldAccountAnimationLoaded,
    setIsOldAccountAnimationLoaded,
  };
};

export default useOldAccountDetails;
